import React, { useState, useEffect } from 'react';
import { 
  Table, Card, Button, Modal, Image, Tag, message, Space,
  Form, Input, Select, Row, Col, Checkbox, Typography, Tabs, Pagination
} from 'antd';
import { 
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, 
  FolderOpenOutlined, SearchOutlined, ReloadOutlined
} from '@ant-design/icons';
import api, { getImageUrl } from '../utils/api';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

const HASH_BATCH_SIZE = 10;
const LIBRARY_PAGE_SIZE = 20;

/** 将数组按固定大小切分，例如 25 个 hash -> [[10],[10],[5]] */
const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/** 按 image_hash 分批（每批最多 10 个）并行查库 */
const lookupMaterialsByHashes = async (imageHashes) => {
  const uniqueHashes = [...new Set(imageHashes.filter(Boolean))];
  if (uniqueHashes.length === 0) {
    return { hashMap: new Map(), batchCount: 0, hashCount: 0 };
  }

  const batches = chunkArray(uniqueHashes, HASH_BATCH_SIZE);
  const responses = await Promise.all(
    batches.map((batch) =>
      api.post('/materials/lookup-by-hashes', { image_hashes: batch })
    )
  );

  const hashMap = new Map();
  for (const response of responses) {
    for (const material of response.data?.items || []) {
      if (material.facebook_hash) {
        hashMap.set(material.facebook_hash, material);
      }
    }
  }

  return { hashMap, batchCount: batches.length, hashCount: uniqueHashes.length };
};

const isVideoMaterial = (material) => {
  if (!material) return false;
  if (material.media_type === 'video') return true;
  const mime = (material.mime_type || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  const name = `${material.name || ''} ${material.file_url || ''} ${material.file_path || ''}`.toLowerCase();
  return /\.(mp4|mov|avi|webm|mkv|m4v|mpeg|mpg)(\?|$)/i.test(name);
};

const MaterialPreview = ({ material }) => {
  const src = getImageUrl(material.file_url);
  if (isVideoMaterial(material)) {
    return (
      <video
        src={src}
        muted
        preload="metadata"
        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4, background: '#000' }}
      />
    );
  }
  return (
    <Image
      src={src}
      alt={material.name}
      preview={false}
      style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}
    />
  );
};

const MaterialCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  
  // 创建素材集相关状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [createTab, setCreateTab] = useState('library');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedMaterialMap, setSelectedMaterialMap] = useState({});

  // 素材库本地搜索
  const [libraryResults, setLibraryResults] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryPagination, setLibraryPagination] = useState({
    current: 1,
    pageSize: LIBRARY_PAGE_SIZE,
    total: 0,
  });
  const [libraryFilters, setLibraryFilters] = useState({
    media_type: undefined,
    name: '',
    hash: '',
  });

  useEffect(() => {
    fetchCollections();
  }, []);

  // 打开创建弹窗时，默认按上传时间分页加载素材库
  useEffect(() => {
    if (createModalVisible) {
      fetchLibraryMaterials(1, LIBRARY_PAGE_SIZE, {
        media_type: undefined,
        name: '',
        hash: '',
      });
    }
  }, [createModalVisible]);

  const resetCreateState = () => {
    createForm.resetFields();
    setSearchResults([]);
    setLibraryResults([]);
    setSelectedMaterials([]);
    setSelectedMaterialMap({});
    setCreateTab('library');
    setLibraryFilters({ media_type: undefined, name: '', hash: '' });
    setLibraryPagination({ current: 1, pageSize: LIBRARY_PAGE_SIZE, total: 0 });
  };

  const fetchLibraryMaterials = async (page = 1, pageSize = LIBRARY_PAGE_SIZE, filters = libraryFilters) => {
    setLibraryLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (filters.name && filters.name.trim()) params.name = filters.name.trim();
      if (filters.hash && filters.hash.trim()) params.hash = filters.hash.trim();
      if (filters.media_type) params.media_type = filters.media_type;

      const response = await api.get('/materials/', { params });
      const data = response.data || {};
      setLibraryResults(data.items || []);
      setLibraryPagination({
        current: data.page || page,
        pageSize: data.page_size || pageSize,
        total: data.total || 0,
      });
    } catch (error) {
      console.error('Failed to search library materials:', error);
      message.error('搜索素材库失败');
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleLibrarySearch = () => {
    const values = createForm.getFieldsValue([
      'library_media_type',
      'library_name',
      'library_hash',
    ]);
    const filters = {
      media_type: values.library_media_type || undefined,
      name: values.library_name || '',
      hash: values.library_hash || '',
    };
    setLibraryFilters(filters);
    fetchLibraryMaterials(1, libraryPagination.pageSize, filters);
  };

  const handleLibraryReset = () => {
    createForm.resetFields(['library_media_type', 'library_name', 'library_hash']);
    const filters = { media_type: undefined, name: '', hash: '' };
    setLibraryFilters(filters);
    fetchLibraryMaterials(1, LIBRARY_PAGE_SIZE, filters);
  };

  // 搜索外部素材
  const handleSearchMaterials = async () => {
    try {
      const values = createForm.getFieldsValue();
      
      const params = {};
      if (values.roi) params.ROI = values.roi;
      if (values.orders) params.orders = values.orders;
      if (values.sevenDaysRoi) params.senvenRoi = values.sevenDaysRoi;
      params.limit = 100;
      
      setSearchLoading(true);
      message.loading('正在搜索素材...', 0);
      
      const queryString = new URLSearchParams(params).toString();
      const fullUrl = `https://www.readgz.com/index/index/sucaiList?${queryString}`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const resultData = await response.json();
      message.destroy();
      
      let externalMaterials = [];
      if (Array.isArray(resultData)) {
        externalMaterials = resultData;
      } else if (resultData.data && Array.isArray(resultData.data)) {
        externalMaterials = resultData.data;
      } else if (resultData.list && Array.isArray(resultData.list)) {
        externalMaterials = resultData.list;
      }

      const imageHashes = externalMaterials
        .map((item) => item.image_hash)
        .filter(Boolean);

      const { hashMap: hashToMaterial, batchCount, hashCount } =
        await lookupMaterialsByHashes(imageHashes);

      const matchedResults = [];
      for (const external of externalMaterials) {
        const matched = hashToMaterial.get(external.image_hash);
        if (matched) {
          matchedResults.push({
            ...matched,
            externalData: external,
          });
        }
      }
      
      setSearchResults(matchedResults);
      if (matchedResults.length > 0) {
        message.success(
          `找到 ${matchedResults.length} 个匹配素材（${hashCount} 个 hash，分 ${batchCount} 批查询）`
        );
      } else {
        message.info(
          hashCount > 0
            ? `未找到匹配素材（已查询 ${hashCount} 个 hash，${batchCount} 批）`
            : '未找到匹配的素材'
        );
      }
      
    } catch (error) {
      console.error('Failed to search materials:', error);
      message.destroy();
      message.error('搜索素材失败: ' + (error.message || '未知错误'));
    } finally {
      setSearchLoading(false);
    }
  };

  const toggleMaterialSelection = (material) => {
    const materialId = material.id;
    setSelectedMaterials((prev) => {
      if (prev.includes(materialId)) {
        return prev.filter((id) => id !== materialId);
      }
      return [...prev, materialId];
    });
    setSelectedMaterialMap((prev) => {
      const next = { ...prev };
      if (next[materialId]) {
        delete next[materialId];
      } else {
        next[materialId] = material;
      }
      return next;
    });
  };

  const toggleAllSelection = (checked, list) => {
    const ids = (list || []).map((m) => m.id);
    if (checked) {
      setSelectedMaterials((prev) => [...new Set([...prev, ...ids])]);
      setSelectedMaterialMap((prev) => {
        const next = { ...prev };
        (list || []).forEach((m) => {
          next[m.id] = m;
        });
        return next;
      });
    } else {
      setSelectedMaterials((prev) => prev.filter((id) => !ids.includes(id)));
      setSelectedMaterialMap((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }
  };

  // 创建素材集
  const handleCreateCollection = async () => {
    try {
      const values = createForm.getFieldsValue();
      
      if (!values.name) {
        message.error('请输入素材集名称');
        return;
      }
      
      if (selectedMaterials.length === 0) {
        message.error('请选择至少一个素材');
        return;
      }
      
      setSearchLoading(true);
      message.loading('正在创建素材集...', 0);
      
      await api.post('/materials/collections/', {
        name: values.name,
        description: values.description,
        material_ids: selectedMaterials
      });
      
      message.destroy();
      message.success('创建成功');
      
      setCreateModalVisible(false);
      resetCreateState();
      fetchCollections(pagination.current, pagination.pageSize);
      
    } catch (error) {
      console.error('Failed to create collection:', error);
      message.destroy();
      message.error('创建失败');
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchCollections = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get('/materials/collections/', {
        params: { page, page_size: pageSize },
      });
      const data = response.data || {};
      setCollections(data.items || []);
      setPagination({
        current: data.page || page,
        pageSize: data.page_size || pageSize,
        total: data.total || 0,
      });
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      message.error('获取素材集列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (collectionId) => {
    try {
      const response = await api.get(`/materials/collections/${collectionId}`);
      setSelectedCollection(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch collection detail:', error);
      message.error('获取素材集详情失败');
    }
  };

  const handleDelete = async (collectionId) => {
    try {
      await api.delete(`/materials/collections/${collectionId}`);
      message.success('删除成功');
      fetchCollections(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to delete collection:', error);
      message.error('删除失败');
    }
  };

  const renderMaterialPickerGrid = (list, { showExternalMetrics = false } = {}) => {
    if (!list || list.length === 0) {
      return (
        <p style={{ textAlign: 'center', color: '#999', padding: 24, margin: 0 }}>
          暂无素材，请调整条件后搜索
        </p>
      );
    }

    const pageIds = list.map((m) => m.id);
    const selectedOnPage = pageIds.filter((id) => selectedMaterials.includes(id));

    return (
      <>
        <div style={{ marginBottom: 12 }}>
          <Checkbox
            checked={selectedOnPage.length === pageIds.length && pageIds.length > 0}
            indeterminate={selectedOnPage.length > 0 && selectedOnPage.length < pageIds.length}
            onChange={(e) => toggleAllSelection(e.target.checked, list)}
          >
            全选本页
          </Checkbox>
          <Text type="secondary" style={{ marginLeft: 12 }}>
            已选 {selectedMaterials.length} 个
          </Text>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, maxHeight: 400, overflowY: 'auto' }}>
          {list.map((material) => (
            <div
              key={material.id}
              style={{
                border: selectedMaterials.includes(material.id) ? '2px solid #1890ff' : '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 8,
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => toggleMaterialSelection(material)}
            >
              {selectedMaterials.includes(material.id) && (
                <div style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  background: '#1890ff',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  zIndex: 1,
                }}>
                  ✓
                </div>
              )}
              <MaterialPreview material={material} />
              <p style={{ marginTop: 8, marginBottom: 4, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {material.name}
              </p>
              <Space size={4} wrap>
                <Tag color={isVideoMaterial(material) ? 'purple' : 'blue'}>
                  {isVideoMaterial(material) ? '视频' : '图片'}
                </Tag>
                {material.facebook_hash && (
                  <Text type="secondary" style={{ fontSize: 11 }} copyable={{ text: material.facebook_hash }}>
                    {(material.facebook_hash || '').slice(0, 10)}…
                  </Text>
                )}
              </Space>
              {showExternalMetrics && material.externalData && (
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  <div>
                    <Text type="secondary">ROI:</Text> {material.externalData.ROI} |
                    <Text type="secondary"> 订单:</Text> {material.externalData.purchase_counts}
                  </div>
                  <div>
                    <Text type="secondary">消耗:</Text> {material.externalData.spends} |
                    <Text type="secondary"> 7日ROI:</Text> {material.externalData.seven_days_roi}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  const columns = [
    {
      title: '素材集名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleViewDetail(record.id)}>
          <FolderOpenOutlined style={{ marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '素材数量',
      dataIndex: 'total_materials',
      key: 'total_materials',
      render: (text) => <span className="badge badge-info">{text}</span>,
    },
    {
      title: '使用次数',
      dataIndex: 'total_usage_count',
      key: 'total_usage_count',
      render: (text) => <span className="badge badge-success">{text || 0}</span>,
    },
    {
      title: '下次分配',
      dataIndex: 'last_material_index',
      key: 'last_material_index',
      render: (text, record) => {
        const total = record.total_materials || 0;
        if (total === 0) return '-';
        return <span>第 {text + 1} 个</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '活跃' : '归档'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleViewDetail(record.id)}
          >
            查看
          </Button>
          <Button icon={<EditOutlined />} size="small">编辑</Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card 
        title="素材集管理" 
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setCreateModalVisible(true);
            resetCreateState();
          }}>
            创建素材集
          </Button>
        }
      >
        <Table
          dataSource={collections}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              fetchCollections(page, pageSize);
            },
          }}
        />
      </Card>

      <Modal
        title={`素材集: ${selectedCollection?.name}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={null}
      >
        {selectedCollection && (
          <div>
            <p><strong>描述:</strong> {selectedCollection.description || '无'}</p>
            <p><strong>素材数量:</strong> {selectedCollection.total_materials}</p>
            <p><strong>总使用次数:</strong> {selectedCollection.total_usage_count || 0} 次</p>
            <p><strong>下次分配起始位置:</strong> 第 {selectedCollection.last_material_index + 1} 个素材</p>
            <p><strong>创建时间:</strong> {new Date(selectedCollection.created_at).toLocaleString('zh-CN')}</p>
            
            <h3 style={{ marginTop: 24, marginBottom: 16 }}>素材列表</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {selectedCollection.materials?.map((material) => (
                <div key={material.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
                  {isVideoMaterial(material) ? (
                    <video
                      src={getImageUrl(material.file_url)}
                      muted
                      preload="metadata"
                      style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, background: '#000' }}
                    />
                  ) : (
                    <Image 
                      src={getImageUrl(material.file_url)} 
                      alt={material.name}
                      style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4 }}
                    />
                  )}
                  <p style={{ marginTop: 8, marginBottom: 4, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {material.name}
                  </p>
                  <Space size={4}>
                    <Tag color={isVideoMaterial(material) ? 'purple' : 'blue'}>
                      {isVideoMaterial(material) ? '视频' : '图片'}
                    </Tag>
                    <Tag color={
                      material.source_type === 'uploaded' ? 'cyan' : 
                      material.source_type === 'duplicate' ? 'orange' : 'default'
                    }>
                      {material.source_type === 'uploaded' ? '上传' : 
                       material.source_type === 'duplicate' ? '重复' : '手动'}
                    </Tag>
                  </Space>
                </div>
              ))}
            </div>
            
            {(!selectedCollection.materials || selectedCollection.materials.length === 0) && (
              <p style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                该素材集暂无素材
              </p>
            )}
          </div>
        )}
      </Modal>
      
      {/* 创建素材集模态框 */}
      <Modal
        title="创建素材集"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          resetCreateState();
        }}
        width={1200}
        footer={[
          <Button key="cancel" onClick={() => {
            setCreateModalVisible(false);
            resetCreateState();
          }}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={searchLoading} onClick={handleCreateCollection}>
            确认创建（已选 {selectedMaterials.length}）
          </Button>
        ]}
      >
        <Form
          form={createForm}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="素材集名称"
                name="name"
                rules={[{ required: true, message: '请输入素材集名称' }]}
              >
                <Input placeholder="请输入素材集名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="描述" name="description">
                <TextArea placeholder="请输入描述（选填）" rows={1} />
              </Form.Item>
            </Col>
          </Row>

          {selectedMaterials.length > 0 && (
            <Card size="small" style={{ marginBottom: 16 }} title={`已选素材（${selectedMaterials.length}）`}>
              <Space wrap>
                {selectedMaterials.map((id) => {
                  const m = selectedMaterialMap[id];
                  return (
                    <Tag
                      key={id}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        if (m) toggleMaterialSelection(m);
                        else {
                          setSelectedMaterials((prev) => prev.filter((x) => x !== id));
                          setSelectedMaterialMap((prev) => {
                            const next = { ...prev };
                            delete next[id];
                            return next;
                          });
                        }
                      }}
                    >
                      {m?.name || `#${id}`}
                    </Tag>
                  );
                })}
              </Space>
            </Card>
          )}
          
          <Tabs activeKey={createTab} onChange={setCreateTab}>
            <TabPane tab="素材库选择" key="library">
              <Card size="small" style={{ marginBottom: 16 }} title="搜索条件">
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label="分类" name="library_media_type">
                      <Select placeholder="全部" allowClear>
                        <Option value="image">图片</Option>
                        <Option value="video">视频</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={9}>
                    <Form.Item label="名称" name="library_name">
                      <Input placeholder="素材名称（模糊）" allowClear />
                    </Form.Item>
                  </Col>
                  <Col span={9}>
                    <Form.Item label="Hash / Video ID" name="library_hash">
                      <Input placeholder="Facebook Hash 或 Video ID（模糊）" allowClear />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify="center">
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      loading={libraryLoading}
                      onClick={handleLibrarySearch}
                    >
                      搜索素材库
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleLibraryReset}>
                      重置并刷新
                    </Button>
                  </Space>
                </Row>
              </Card>

              <Card
                size="small"
                title={`素材库（按上传时间，共 ${libraryPagination.total}）`}
                loading={libraryLoading}
              >
                {renderMaterialPickerGrid(libraryResults)}
                {libraryPagination.total > 0 && (
                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Pagination
                      current={libraryPagination.current}
                      pageSize={libraryPagination.pageSize}
                      total={libraryPagination.total}
                      showSizeChanger
                      showQuickJumper
                      showTotal={(total) => `共 ${total} 条`}
                      onChange={(page, pageSize) => {
                        fetchLibraryMaterials(page, pageSize, libraryFilters);
                      }}
                    />
                  </div>
                )}
              </Card>
            </TabPane>

            <TabPane tab="外部匹配" key="external">
              <Card size="small" style={{ marginBottom: 16 }} title="外部筛选条件">
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="总ROI" name="roi">
                      <Input type="number" placeholder="总ROI筛选值" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="订单数" name="orders">
                      <Input type="number" placeholder="订单数筛选值" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="七日ROI" name="sevenDaysRoi">
                      <Input type="number" placeholder="七日ROI筛选值" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify="center">
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />} loading={searchLoading} onClick={handleSearchMaterials}>
                      搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => {
                      createForm.resetFields(['roi', 'orders', 'sevenDaysRoi']);
                      setSearchResults([]);
                    }}>
                      重置
                    </Button>
                  </Space>
                </Row>
              </Card>

              {searchResults.length > 0 && (
                <Card size="small" title={`外部匹配结果（${searchResults.length}）`}>
                  {renderMaterialPickerGrid(searchResults, { showExternalMetrics: true })}
                </Card>
              )}
            </TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default MaterialCollections;
